import React from 'react';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, Eye, CreditCard, TrendingUp,
  Crown, Clock, Trash2, Shield, ShieldOff, BarChart3,
  Link2, Percent, Euro, Plus, Copy, Check as CheckIcon, X,
  Ban, UserCheck, Gift, CalendarPlus, Download, History, MoreVertical, Mail, Send, Tag
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { TabsContent } from '../../ui/tabs';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';

export const UsersTab = ({ ctx }) => {
  const { user, users, handleToggleAdmin, handleToggleBlock, handleDeleteUser, handleViewActivity, handleExportUsers, openUserModal } = ctx;
  return (
  <TabsContent value="users" className="space-y-4">
              {/* Export Button */}
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Gebruikersbeheer</h2>
                <Button
                  data-testid="export-users-btn"
                  variant="outline"
                  onClick={handleExportUsers}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exporteer CSV
                </Button>
              </div>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {users.map(u => (
                      <div key={u.id} className={`p-4 flex items-center justify-between hover:bg-muted/30 transition-colors ${u.is_blocked ? 'bg-red-50/50' : ''}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            u.is_blocked ? 'bg-red-100 text-red-600' :
                            u.is_admin ? 'bg-gold/10 text-gold' : 'bg-muted text-muted-foreground'
                          }`}>
                            {u.is_blocked ? <Ban className="w-5 h-5" /> :
                             u.is_admin ? <Shield className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{u.name}</p>
                              {u.is_blocked && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Geblokkeerd</span>
                              )}
                              {u.is_admin && (
                                <span className="text-xs bg-gold/10 text-gold px-2 py-0.5 rounded-full">Admin</span>
                              )}
                              {u.subscription?.status === 'active' && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Crown className="w-3 h-3" /> Premium
                                </span>
                              )}
                              {u.subscription?.status === 'trial' && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Trial
                                </span>
                              )}
                              {u.subscription?.status === 'canceled' && (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Geannuleerd</span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            {u.grandma && (
                              <p className="text-xs text-muted-foreground mt-1">Oma: {u.grandma.name}</p>
                            )}
                            {u.subscription?.subscription_end_date && (
                              <p className="text-xs text-muted-foreground">
                                Eindigt: {new Date(u.subscription.subscription_end_date).toLocaleDateString('nl-NL')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* User Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`user-actions-${u.id}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem
                                onClick={() => openUserModal(u)}
                                className="cursor-pointer"
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                Abonnement beheren
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleViewActivity(u.id)}
                                className="cursor-pointer"
                              >
                                <History className="w-4 h-4 mr-2" />
                                Activiteit bekijken
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleToggleBlock(u.id)}
                                disabled={u.id === user?.id}
                                className="cursor-pointer"
                              >
                                {u.is_blocked ? (
                                  <>
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Deblokkeren
                                  </>
                                ) : (
                                  <>
                                    <Ban className="w-4 h-4 mr-2" />
                                    Blokkeren
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleToggleAdmin(u.id)}
                                disabled={u.id === user?.id}
                                className="cursor-pointer"
                              >
                                {u.is_admin ? (
                                  <>
                                    <ShieldOff className="w-4 h-4 mr-2" />
                                    Admin verwijderen
                                  </>
                                ) : (
                                  <>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Admin maken
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDeleteUser(u.id)}
                                disabled={u.id === user?.id}
                                className="cursor-pointer text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
  );
};
